import { message } from 'antd'
import { getRequiredSections, Report } from 'features/reporting/types'
import { VisualInspectionFormDataState } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useCallback, useMemo, useState } from 'react'

interface SectionStatus {
  isComplete: boolean
  completedFields: number
  totalFields: number
}

/**
 * Calculate completion status for a section based on required fields
 */
const calculateSectionCompletion = (
  requiredFields: string[],
  data: Record<string, unknown> | undefined
): SectionStatus => {
  if (!data) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: requiredFields.length,
    }
  }
  let completedFields = 0
  const totalFields = requiredFields.length

  requiredFields.forEach((field) => {
    const value = data[field]
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length > 0) {
        completedFields++
      } else if (!Array.isArray(value)) {
        completedFields++
      }
    }
  })

  return {
    isComplete: completedFields > 0 && completedFields === totalFields,
    completedFields,
    totalFields,
  }
}

/**
 * Get completion status for visual inspection report sections
 */
const getVisualReportCompletionStatus = (
  formData: VisualInspectionFormDataState
): Record<string, SectionStatus> => {
  const status: Record<string, SectionStatus> = {}

  const fallbackStatus: SectionStatus = {
    isComplete: false,
    completedFields: 0,
    totalFields: 0,
  }

  // Cover section (required)
  const coverFields = ['asset', 'assetId', 'inspectionType']
  status.cover = calculateSectionCompletion(coverFields, {
    asset: formData.coverSectionData?.asset,
    assetId: formData.coverSectionData?.assetId,
    inspectionType: formData.coverSectionData?.inspectionType,
  })

  // Checklist findings (optional, but if started, scope is required)
  if (formData.checklistFindingsData?.scope !== undefined) {
    status.checklistFindings = calculateSectionCompletion(['scope'], {
      scope: formData.checklistFindingsData.scope,
    })
  } else {
    status.checklistFindings = {
      ...fallbackStatus,
    }
  }

  // Asset corrosion analysis (optional, but if started, analysis is required)
  if (formData.assetCorrosionAnalysisData?.analysis !== undefined) {
    status.assetCorrosionAnalysis = calculateSectionCompletion(['analysis'], {
      analysis: formData.assetCorrosionAnalysisData.analysis,
    })
  } else {
    status.assetCorrosionAnalysis = {
      ...fallbackStatus,
    }
  }

  // Findings (required)
  status.findings = calculateSectionCompletion(['findings'], {
    findings: formData.findingsData?.findings,
  })

  // Recommendations (optional)
  if (formData.recommendationsData?.recommendations !== undefined) {
    const hasRecommendations =
      formData.recommendationsData.recommendations.length > 0
    status.recommendations = {
      isComplete: hasRecommendations,
      completedFields: hasRecommendations ? 1 : 0,
      totalFields: 1,
    }
  } else {
    status.recommendations = {
      ...fallbackStatus,
    }
  }

  // Pressure test sub-inspections (optional)
  if (
    formData.pressureTestSubInspectionsData?.pressureTestSubInspection !==
    undefined
  ) {
    status.pressureTestSubInspections = calculateSectionCompletion(
      ['pressureTestSubInspection'],
      {
        pressureTestSubInspection:
          formData.pressureTestSubInspectionsData.pressureTestSubInspection,
      }
    )
  } else {
    status.pressureTestSubInspections = {
      ...fallbackStatus,
    }
  }

  // Bundle sub-inspections (optional)
  if (formData.bundleSubInspectionsData?.bundleSubInspection !== undefined) {
    status.bundleSubInspections = calculateSectionCompletion(
      ['bundleSubInspection'],
      {
        bundleSubInspection:
          formData.bundleSubInspectionsData.bundleSubInspection,
      }
    )
  } else {
    status.bundleSubInspections = {
      ...fallbackStatus,
    }
  }

  // Inspection team (required)
  const hasTeamMembers =
    formData.inspectionTeamMemberData?.inspectionTeamMembers &&
    formData.inspectionTeamMemberData.inspectionTeamMembers.length > 0
  status.inspectionTeam = {
    isComplete: !!hasTeamMembers,
    completedFields: hasTeamMembers ? 1 : 0,
    totalFields: 1,
  }

  // Reference documents (optional)
  if (formData.referenceDocumentsData?.referenceDocuments !== undefined) {
    const hasDocuments =
      formData.referenceDocumentsData.referenceDocuments.length > 0
    status.referenceDocuments = {
      isComplete: hasDocuments,
      completedFields: hasDocuments ? 1 : 0,
      totalFields: 1,
    }
  } else {
    status.referenceDocuments = {
      ...fallbackStatus,
    }
  }

  // Review section (checking signoff)
  const supervisorSigned = !!formData.signoff?.supervisorSignoff
  const reviewerSigned = !!formData.signoff?.reviewerSignoff
  const signoffCount = Number(supervisorSigned) + Number(reviewerSigned)
  status.review = {
    isComplete: supervisorSigned && reviewerSigned,
    completedFields: signoffCount,
    totalFields: 2,
  }

  // PDF Generation section (always complete)
  status.pdfGeneration = {
    ...fallbackStatus,
  }

  return status
}

interface UseReportNavigationResult {
  activePage: string | null
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  canNavigate: (page: string) => boolean
  completionStatus: Record<
    string,
    {
      isComplete: boolean
      completedFields: number
      totalFields: number
    }
  >
}

interface UseReportNavigationProps {
  formData: VisualInspectionFormDataState
  report: Report
}

/**
 * Hook to manage report navigation and page completion status for visual inspection reports
 */
export const useReportNavigation = ({
  formData,
  report,
}: UseReportNavigationProps): UseReportNavigationResult => {
  const [activePage, setActivePage] = useState<string | null>(null)

  // Calculate completion status for all sections
  const completionStatus = useMemo(
    () => getVisualReportCompletionStatus(formData),
    [formData]
  )

  /**
   * Check if navigation to a page is allowed based on dependencies
   */
  const canNavigate = useCallback(
    (page: string): boolean => {
      // For review page, check if all required sections are complete
      if (page === 'review') {
        const requiredSections: string[] = getRequiredSections(report)
        const hasIncomplete = requiredSections.some(
          (section) => !completionStatus[section]?.isComplete
        )

        if (hasIncomplete) {
          message.warning('Please complete all required sections before review')
          // Commented out for development purposes
          // return false
        }
      }

      return true
    },
    [completionStatus, report]
  )

  /**
   * Navigate to a specific page
   */
  const navigateToPage = useCallback(
    (page: string) => {
      if (canNavigate(page)) {
        setActivePage(page)
      }
    },
    [canNavigate]
  )

  /**
   * Return to the main menu
   */
  const returnToMenu = useCallback(() => {
    setActivePage(null)
  }, [setActivePage])

  return {
    activePage,
    navigateToPage,
    returnToMenu,
    canNavigate,
    completionStatus,
  }
}
